import { useLogout, useGetIdentity } from '@refinedev/core';
import { Link, useLocation } from 'react-router';
import {
  Home,
  BedDouble,
  Utensils,
  Calendar,
  Map,
  Compass,
  Activity,
  LogOut,
  Menu,
  X,
  Image,
  Layout as LayoutIcon,
  Settings,
  Building2,
  Package,
  Tag,
  FileText,
  Mountain
} from 'lucide-react';
import { useState } from 'react';

const menuGroups = [
  {
    label: 'Dashboard',
    items: [
      { name: 'Dashboard', path: '/', icon: Home },
    ],
  },
  {
    label: 'Content',
    items: [
      { name: 'Rooms', path: '/rooms', icon: BedDouble },
      { name: 'Activities', path: '/activities', icon: Activity },
      { name: 'Dining', path: '/dining', icon: Utensils },
      { name: 'Events', path: '/events', icon: Calendar },
      { name: 'Tours', path: '/tours', icon: Compass },
      { name: 'Attractions', path: '/attractions', icon: Map },
      { name: 'Outbound Packages', path: '/outbound-packages', icon: Mountain },
      { name: 'Offers & Packages', path: '/offers', icon: Tag },
      { name: 'Pages', path: '/pages', icon: FileText },
    ],
  },
  {
    label: 'Meetings',
    items: [
      { name: 'Conference Rooms', path: '/conference-rooms', icon: Building2 },
      { name: 'Meeting Packages', path: '/meeting-packages', icon: Package },
    ],
  },
  {
    label: 'Homepage',
    items: [
      { name: 'Hero Slides', path: '/hero-slides', icon: Image },
      { name: 'Home Sections', path: '/home-sections', icon: LayoutIcon },
    ],
  },
  {
    label: 'Settings',
    items: [
      { name: 'Site Settings', path: '/site-settings', icon: Settings },
    ],
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const { mutate: logout } = useLogout();
  const { data: identity } = useGetIdentity<{ name: string }>();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white shadow-lg transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <h1 className="text-xl font-bold text-gold-600">Natsepa Admin</h1>
          <button
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
          {menuGroups.map((group, groupIndex) => (
            <div key={group.label}>
              {groupIndex > 0 && (
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-4">
                  {group.label}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path ||
                    (item.path !== '/' && location.pathname.startsWith(item.path));

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors
                        ${isActive
                          ? 'bg-gold-50 text-gold-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button
            onClick={() => logout()}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="h-16 bg-white shadow-sm flex items-center justify-between px-4">
          <button
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="ml-auto flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Welcome, {identity?.name || 'Admin'}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
